import { useMemo } from 'react';
import { css } from '@emotion/react';

import { MentoringProgramRole } from 'redux/schemas/models/mentoring-program-enrollments';
import {
  MentoringProgramReflectionParticipantResponses,
  MentoringProgramReflectionQuestion,
} from 'redux/schemas/models/mentoring-program-reflections';
import { User } from 'redux/schemas/models/my-account';
import { standardSpacing } from 'styles/global_defaults/scaffolding';
import ReflectionResponseCard from './reflection-response-card';

const questionGroupStyles = css`
  gap: ${standardSpacing}px;
`;

type ReflectionReadViewProps = {
  currentEnrollmentQuestions: MentoringProgramReflectionQuestion[];
  connectionEnrollmentQuestions: MentoringProgramReflectionQuestion[];
  currentEnrollmentResponses: MentoringProgramReflectionParticipantResponses;
  connectionEnrollmentResponses: MentoringProgramReflectionParticipantResponses | null;
  currentEnrollmentUser: User | undefined;
  currentEnrollmentRole: MentoringProgramRole;
  connectionEnrollmentUser: User | undefined;
  connectionEnrollmentRole: MentoringProgramRole;
};

const ReflectionReadView = ({
  currentEnrollmentQuestions,
  connectionEnrollmentQuestions,
  currentEnrollmentResponses,
  connectionEnrollmentResponses,
  currentEnrollmentUser,
  currentEnrollmentRole,
  connectionEnrollmentUser,
  connectionEnrollmentRole,
}: ReflectionReadViewProps) => {
  const sortedQuestions = useMemo(
    () => [...currentEnrollmentQuestions].sort((a, b) => a.questionIndex - b.questionIndex),
    [currentEnrollmentQuestions],
  );

  const currentEnrollmentQuestionTextById = useMemo(
    () => Object.fromEntries(currentEnrollmentQuestions.map(q => [q.id, q.questionText])),
    [currentEnrollmentQuestions],
  );

  const connectionEnrollmentQuestionTextById = useMemo(
    () => Object.fromEntries(connectionEnrollmentQuestions.map(q => [q.id, q.questionText])),
    [connectionEnrollmentQuestions],
  );

  const currentEnrollmentResponsesByQuestionId = useMemo(
    () => Object.fromEntries(currentEnrollmentResponses.answers.map(a => [a.questionId, a.response])),
    [currentEnrollmentResponses],
  );

  const connectionEnrollmentResponsesByQuestionId = useMemo(
    () => Object.fromEntries((connectionEnrollmentResponses?.answers ?? []).map(a => [a.questionId, a.response])),
    [connectionEnrollmentResponses],
  );

  const isCurrentEnrollmentMentor =
    currentEnrollmentRole === MentoringProgramRole.MENTORSHIP_PROGRAM_MENTOR;
  const mentorUser = isCurrentEnrollmentMentor ? currentEnrollmentUser : connectionEnrollmentUser;
  const menteeUser = isCurrentEnrollmentMentor ? connectionEnrollmentUser : currentEnrollmentUser;

  const mentorQuestionTextById = isCurrentEnrollmentMentor
    ? currentEnrollmentQuestionTextById
    : connectionEnrollmentQuestionTextById;
  const menteeQuestionTextById = isCurrentEnrollmentMentor
    ? connectionEnrollmentQuestionTextById
    : currentEnrollmentQuestionTextById;

  return (
    <>
      {sortedQuestions.map(({ id }, questionIndex) => {
        const mentorAnswer = isCurrentEnrollmentMentor
          ? currentEnrollmentResponsesByQuestionId[id]
          : connectionEnrollmentResponsesByQuestionId[id];
        const menteeAnswer = isCurrentEnrollmentMentor
          ? connectionEnrollmentResponsesByQuestionId[id]
          : currentEnrollmentResponsesByQuestionId[id];

        return (
          <div key={id} css={questionGroupStyles} className='d-flex flex-column'>
            {mentorAnswer && mentorUser && (
              <ReflectionResponseCard
                questionText={mentorQuestionTextById[id]}
                response={mentorAnswer}
                user={mentorUser}
                role={MentoringProgramRole.MENTORSHIP_PROGRAM_MENTOR}
                index={questionIndex}
              />
            )}
            {menteeAnswer && menteeUser && (
              <ReflectionResponseCard
                questionText={menteeQuestionTextById[id]}
                response={menteeAnswer}
                user={menteeUser}
                role={MentoringProgramRole.MENTORSHIP_PROGRAM_MENTEE}
                index={questionIndex}
              />
            )}
          </div>
        );
      })}
    </>
  );
};

export default ReflectionReadView;
